import { useUpdateCarrierMutation } from "@qw/graphql";
import { Button, FieldInput, FieldSwitch, Form, Group, useToast } from "@qw/ui";
import { z } from "zod";

const carrierSchema = z.object({
  url: z.string().nullable().optional(),
  portalUrl: z.string().nullable().optional(),
  qwAppointed: z.boolean(),
  phone: z.string().nullable().optional(),
  fax: z.string().nullable().optional(),
  email: z.string().nullable().optional(),
  addressLine1: z.string().nullable().optional(),
  addressLine2: z.string().nullable().optional(),
  city: z.string().nullable().optional(),
  state: z.string().nullable().optional(),
  postalCode: z.string().nullable().optional(),
});

type CarrierFormValues = z.infer<typeof carrierSchema>;

interface CarrierFormProps {
  carrier: {
    id: string;
    url?: string | null;
    portalUrl?: string | null;
    qwAppointed: boolean;
    bindHqMarketingCompanyId?: string | null;
    bindHqMarketingCompanyName?: string | null;
    phone?: string | null;
    fax?: string | null;
    email?: string | null;
    addressLine1?: string | null;
    addressLine2?: string | null;
    city?: string | null;
    state?: string | null;
    postalCode?: string | null;
  };
}

export function CarrierForm({ carrier }: CarrierFormProps) {
  const { toast } = useToast();
  const [updateCarrier] = useUpdateCarrierMutation();

  const handleSubmit = async (values: CarrierFormValues) => {
    try {
      await updateCarrier({
        variables: {
          input: {
            id: carrier.id,
            ...values,
          },
        },
      });
      toast({ title: "Carrier updated" });
    } catch (error) {
      toast({ title: "Error", description: String((error as Error).message ?? error), variant: "destructive" });
    }
  };

  return (
    <Form
      validationSchema={carrierSchema}
      defaultValues={{
        url: carrier.url ?? "",
        portalUrl: carrier.portalUrl ?? "",
        qwAppointed: carrier.qwAppointed,
        phone: carrier.phone ?? "",
        fax: carrier.fax ?? "",
        email: carrier.email ?? "",
        addressLine1: carrier.addressLine1 ?? "",
        addressLine2: carrier.addressLine2 ?? "",
        city: carrier.city ?? "",
        state: carrier.state ?? "",
        postalCode: carrier.postalCode ?? "",
      }}
      onSubmit={handleSubmit}
    >
      <>
        <Group type="flex" direction="row" className="gap-4 items-start mb-4">
          <Group type="flex" direction="col" className="flex-1 gap-2">
            <FieldInput label="Website URL" name="url" placeholder="https://carrier.com" />
            <FieldInput label="Portal URL" name="portalUrl" placeholder="https://portal.carrier.com" />
            <FieldSwitch label="QW Appointed" name="qwAppointed" />
            <ReadOnlyValue label="BindHQ Marketing Company ID" value={carrier.bindHqMarketingCompanyId} />
            <ReadOnlyValue label="BindHQ Marketing Company Name" value={carrier.bindHqMarketingCompanyName} />
            <FieldInput name="phone" label="Phone" placeholder="Phone number" />
            <FieldInput name="fax" label="Fax" placeholder="Fax number" />
            <FieldInput name="email" label="Email" placeholder="Email address" />
          </Group>

          <Group type="flex" direction="col" className="flex-1 gap-2">
            <FieldInput name="addressLine1" label="Address Line 1" placeholder="Street address" />
            <FieldInput name="addressLine2" label="Address Line 2" placeholder="Suite, unit, etc." />
            <div className="grid grid-cols-3 gap-4">
              <FieldInput name="city" label="City" placeholder="City" />
              <FieldInput name="state" label="State" placeholder="State" />
              <FieldInput name="postalCode" label="Postal Code" placeholder="ZIP code" />
            </div>
          </Group>
        </Group>
        <Button type="submit">Save</Button>
      </>
    </Form>
  );
}

function ReadOnlyValue({ label, value }: { label: string; value?: string | null }) {
  return (
    <div className="flex flex-col gap-1.5">
      <span className="text-xs font-semibold text-muted-foreground">{label}</span>
      <span className="text-sm">{value || "—"}</span>
    </div>
  );
}
